/**
 * 連携中メールアカウントの表示カード.
 */

import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import type { EmailConnection } from "hooks/use-email-integrations-api";
import type React from "react";
import { useMemo } from "react";
import ProviderIcon from "routes/profile/components/ProviderIcon";
import { formatDateTime } from "utils/relative-time";

const PROVIDER_LABEL: Record<string, string> = {
  gmail: "Gmail",
  outlook: "Outlook",
};

const STATUS_DISPLAY: Record<string, { label: string; tone: BadgeTone }> = {
  connected: { label: "連携済み", tone: "soft-success" },
  disconnected: { label: "再連携が必要", tone: "soft-warning" },
  error: { label: "エラー", tone: "soft-error" },
};

/** 同期が遅延しているとみなす閾値 (ms). 1 時間以上 last_synced_at が更新されない場合に警告. */
const STALE_SYNC_THRESHOLD_MS = 60 * 60 * 1000;

type Props = {
  connection: EmailConnection;
  onDisconnect: (id: string) => Promise<void> | void;
  onReauth: (provider: string) => Promise<void> | void;
  isDisconnecting?: boolean;
  isReauthing?: boolean;
};

/**
 * 連携 1 件分のカード UI.
 * @param {Props} props プロパティ
 * @return {JSX.Element} カード
 */
const EmailConnectionCard: React.FC<Props> = ({
  connection,
  onDisconnect,
  onReauth,
  isDisconnecting,
  isReauthing,
}) => {
  const providerLabel =
    PROVIDER_LABEL[connection.provider] ?? connection.provider;
  const statusInfo = STATUS_DISPLAY[connection.status] ?? {
    label: connection.status,
    tone: "soft-info" as BadgeTone,
  };

  const lastSynced = formatDateTime(connection.lastSyncedAt);

  // ISSUE-824: 「連携済み」だが last_synced_at が長時間更新されていない場合に
  // 同期遅延の警告を出す。 取り込みパイプラインが詰まっていることをユーザーに
  // 通知し、 再認証 / 解除のきっかけにする。
  const isSyncStale = useMemo(() => {
    if (connection.status !== "connected") return false;
    if (!connection.lastSyncedAt) return false;
    const last = new Date(connection.lastSyncedAt).getTime();
    if (Number.isNaN(last)) return false;
    return Date.now() - last > STALE_SYNC_THRESHOLD_MS;
  }, [connection.status, connection.lastSyncedAt]);

  const isReauthPrimary =
    connection.status === "disconnected" || connection.status === "error";

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {connection.profilePictureUrl ? (
            <img
              src={connection.profilePictureUrl}
              alt=""
              className="w-10 h-10 rounded-full border border-gray-200"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
              {providerLabel.charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ProviderIcon provider={connection.provider} size={18} />
              <span className="font-medium truncate">
                {connection.displayName || connection.emailAddress}
              </span>
              <span className="text-xs text-gray-400">{providerLabel}</span>
            </div>
            <div className="text-sm text-gray-600 truncate">
              {connection.emailAddress}
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              <StatusBadge tone={statusInfo.tone}>
                {statusInfo.label}
              </StatusBadge>
              {isSyncStale && (
                <StatusBadge tone="soft-warning">同期遅延</StatusBadge>
              )}
              <span>最終同期: {lastSynced}</span>
            </div>
            {connection.errorMessage && (
              <div className="mt-1 text-xs text-error">
                {connection.errorMessage}
              </div>
            )}
            {isSyncStale && !connection.errorMessage && (
              <div className="mt-1 text-xs text-amber-700">
                取り込みが 1 時間以上停止しています。
                「再認証」を試してください。
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <Button
            variant={isReauthPrimary ? "default" : "outline"}
            size="sm"
            onClick={() => onReauth(connection.provider)}
            disabled={isReauthing || isDisconnecting}
          >
            {isReauthing ? (
              <>
                <Loading size="xs" />
                リダイレクト中…
              </>
            ) : (
              "再認証"
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDisconnect(connection.id)}
            disabled={isDisconnecting || isReauthing}
          >
            {isDisconnecting ? (
              <>
                <Loading size="xs" />
                解除中…
              </>
            ) : (
              "連携解除"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailConnectionCard;
